import { Table, Box, SpaceBetween, Link, PropertyFilter, StatusIndicator, Pagination } from '@amzn/awsui-components-react/polaris';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CalculationComment, CalculationReviewApplyCommentType, OpenCalculationReview, OpenCalculationReviewsResult, SaveOpenCalculationReviewNotificationCommentRequest } from 'src/models/dashboard/OpenCalculationReview';
import EntityStatusUtil from 'src/services/common/EntityStatusUtil';
import TaskStatusIndicator from '../shared/TaskStatusIndicator';
import CONSTANTS from 'src/utils/constants';
import TPENavigator from '../TPENavigator';
import DASHBOARD_CONSTANTS from './dashboardConstants';
import { convertObjectsToFilterOptions, getDefaultDateFilterOperators, OPERATORS_FOR_STRING_PROPERTY, replaceFilterAccessorFieldWithBDField } from './helpers/PropertyFilterHelper';
import { PropertyFilterQuery } from '@amzn/awsui-collection-hooks';
import OpenCalculationReviewCommentModal from './OpenCalculationReviewCommentModal';
import { GlobalAppContext } from '../App';
import { DbSortOrder } from 'src/models/common/TPESearchRequest';
import { formatAccountingDueDate } from 'src/components/accounting-calendar/mecDayLabel';

export default function OpenCalculationReviewsGrid(props: {
    openCalculationReviewsResult: OpenCalculationReviewsResult,
    isLoading: boolean,
    dashboardView?: boolean,
    gridHeader?: any,
    showFilter?: boolean,
    onFilterQueryChanged?: (query: PropertyFilterQuery) => void
    onPageChanged?: (page: number) => void
    onSortingChanged?: (sortOrder: string, sortField?:string) => void
}) {
    const { openCalculationReviewsResult, isLoading, dashboardView = false, gridHeader, showFilter = false, onFilterQueryChanged, onPageChanged, onSortingChanged } = props;
    const { services } = useContext(GlobalAppContext);
    const [navigateURL, setNavigateURL] = useState('');
    const [sortingDescending, setSortingDescending] = useState<boolean|undefined>(false);
    const [sortingColumn, setSortingColumn] = useState<any>(null);
    const [reviewItemForComments, setReviewItemForComments] = useState({} as OpenCalculationReview);
    const [showCommentsModal, setShowCommentsModal] = useState(false);
    const [refreshComments, setRefreshComments] = useState(true);
    const [saveCommentPayload, setSaveCommentPayload] = useState<SaveOpenCalculationReviewNotificationCommentRequest | null>(null);

    const [saveCommentResult, isSavingComment, saveCommentError] = services.dashboardService.saveNotificationComment(saveCommentPayload);
    const [getCommentsResult, loadingComments, getCommentsError] = services.dashboardService.getCLIReviewNotificationComments(refreshComments, reviewItemForComments?.calculationNumber);

    const navigateToCalcBuilder = (calculationNumber: string, recordId: string) => {
        if (recordId != null) {
            setNavigateURL(CONSTANTS.PAGE_NAV.CALCULATION_BUILDER.URL + '/' + btoa(recordId));
        } else {
            setNavigateURL(CONSTANTS.PAGE_NAV.ICRS_CALCULATION_BUILDER.URL + '/' + btoa(calculationNumber));
        }
    }

    const onCommentsButtonClicked = (item: OpenCalculationReview) => {
        setReviewItemForComments(item);
        setShowCommentsModal(true);
    }

    const colDefs: any[] = useMemo(() => [
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.WORKBOOK_NAME.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.WORKBOOK_NAME.HEADER,
          cell: (item: OpenCalculationReview) => item.workbookName,
          width: '20%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.WORKBOOK_NAME.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.CALCULATION_NUMBER.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.CALCULATION_NUMBER.HEADER,
          cell: (item: OpenCalculationReview) => <Link href="javascript:void(0);" onFollow={() => navigateToCalcBuilder(item.calculationNumber, item.recordId)}>{item.calculationNumber}</Link>,
          width: '15%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.CALCULATION_NUMBER.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ATP_CALCULATION_STATUS.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ATP_CALCULATION_STATUS.HEADER,
          cell: (item: OpenCalculationReview) => <StatusIndicator type={EntityStatusUtil.mapToStatusIndicatorBy(item.atpCalculationStatus)}>{item.atpCalculationStatus}</StatusIndicator>,
          width: '15%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ATP_CALCULATION_STATUS.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.APTTUS_CALCULATION_STATUS.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.APTTUS_CALCULATION_STATUS.HEADER,
          cell: (item: OpenCalculationReview) => <StatusIndicator type={EntityStatusUtil.mapToStatusIndicatorBy(item.apttusCalculationStatus)}>{item.apttusCalculationStatus}</StatusIndicator>,
          width: '15%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.APTTUS_CALCULATION_STATUS.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.TASK_STATUS.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.TASK_STATUS.HEADER,
          cell: (item: OpenCalculationReview) => <TaskStatusIndicator status={item.taskStatus} />,
          width: '10%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.TASK_STATUS.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ACCOUNTING_DAY.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ACCOUNTING_DAY.HEADER,
          cell: (item: OpenCalculationReview) => formatAccountingDueDate(item.dueDateWorkDayNumber, item.dueDateDisplay),
          width: '12%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ACCOUNTING_DAY.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.DUE_DATE.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.DUE_DATE.HEADER,
          cell: (item: OpenCalculationReview) => item.dueDateDisplay,
          width: '12%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.DUE_DATE.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.OWNER.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.OWNER.HEADER,
          cell: (item: OpenCalculationReview) => item.owner,
          width: '8%',
          sortingField: !onSortingChanged? null: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.OWNER.ACCESSOR
      },
      {
          id: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.COMMENTS.ACCESSOR,
          header: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.COMMENTS.HEADER,
          cell: (item: OpenCalculationReview) => 
            DASHBOARD_CONSTANTS.COMPLETE_STATUS === item.taskStatus && !item.hasComments? 
                null : 
                <Link onClick={() => onCommentsButtonClicked(item)}>{item.hasComments? "View" : "Add"}</Link>,
          width: '5%'
      }
    ], [openCalculationReviewsResult]);

    const filteringProperties = useMemo(() => [
    {
        key: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.WORKBOOK_NAME.ACCESSOR,
        operators: OPERATORS_FOR_STRING_PROPERTY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.WORKBOOK_NAME.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.WORKBOOK_NAME.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.CALCULATION_NUMBER.ACCESSOR,
        operators: OPERATORS_FOR_STRING_PROPERTY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.CALCULATION_NUMBER.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.CALCULATION_NUMBER.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ATP_CALCULATION_STATUS.ACCESSOR,
        operators: OPERATORS_FOR_STRING_PROPERTY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ATP_CALCULATION_STATUS.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.ATP_CALCULATION_STATUS.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.APTTUS_CALCULATION_STATUS.ACCESSOR,
        operators: OPERATORS_FOR_STRING_PROPERTY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.APTTUS_CALCULATION_STATUS.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.APTTUS_CALCULATION_STATUS.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.TASK_STATUS.ACCESSOR,
        operators: OPERATORS_FOR_STRING_PROPERTY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.TASK_STATUS.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.TASK_STATUS.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.DUE_DATE.ACCESSOR,
        operators: getDefaultDateFilterOperators(),
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.DUE_DATE.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.DUE_DATE.HEADER} values`
    },
    {
        key: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.OWNER.ACCESSOR,
        operators: OPERATORS_FOR_STRING_PROPERTY,
        propertyLabel: DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.OWNER.HEADER,
        groupValuesLabel: `${DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS.OWNER.HEADER} values`
    },
    ], [])

    const filteringOptions = useMemo(
        () => convertObjectsToFilterOptions(openCalculationReviewsResult?.items || []),
    [openCalculationReviewsResult]);

    const [filterQuery, setFilteringQuery] = useState({
        tokens: [],
        operation: "and"
    } as PropertyFilterQuery);

    useEffect(() => {
        if (onFilterQueryChanged){
            onFilterQueryChanged(replaceFilterAccessorFieldWithBDField(filterQuery, DASHBOARD_CONSTANTS.OPEN_CALCULATION_REVIEW_FIELDS));
        }
    }, [filterQuery])

    useEffect(() => {
        if (getCommentsResult.length > 0){
            setRefreshComments(false);
        }
    }, [getCommentsResult])
    
    useEffect(() => {
        if (saveCommentResult) {
            setSaveCommentPayload(null);
            setRefreshComments(true);
        }
    }, [saveCommentResult])

    const onPageChangedHandler = (currentPageIndex: number) => {
        if (onPageChanged){
            onPageChanged(currentPageIndex);
        }
    }

    const items = openCalculationReviewsResult?.items || [];

    function saveComment(calculationReview: OpenCalculationReview, comment: CalculationComment): void {
        setSaveCommentPayload({
            calculationNumber: calculationReview.calculationNumber,
            comment: comment.comment,
            applyCommentToCWB: comment.applyCommentType === CalculationReviewApplyCommentType.CWB,
            applyCommentToTemplate: comment.applyCommentType === CalculationReviewApplyCommentType.Template
        } as SaveOpenCalculationReviewNotificationCommentRequest);
    }

    return (
    <>
        <TPENavigator path={navigateURL} />
        <Table
            columnDefinitions={colDefs}
            enableKeyboardNavigation
            wrapLines={true}
            items={items}
            loadingText="Loading"
            loading={isLoading}
            header={gridHeader}
            sortingDescending={sortingDescending}
            sortingColumn={sortingColumn}
            empty={
                <Box
                margin={{ vertical: "xs" }}
                textAlign="center"
                color="inherit"
                >
                <SpaceBetween size="m">
                    <b>No open CLI reviews</b>
                    <span>All CLI reviews have been completed</span>
                </SpaceBetween>
                </Box>
            }
            pagination={
                openCalculationReviewsResult?.pageSize === DASHBOARD_CONSTANTS.SMALL_PAGE_SIZE? null : 
                <Pagination 
                    currentPageIndex={openCalculationReviewsResult?.page} 
                    pagesCount={openCalculationReviewsResult?.pagesCount}
                    onChange={({ detail }) =>
                        onPageChangedHandler(detail.currentPageIndex)
                      }
                     />
              }
            variant={dashboardView ? 'embedded' : 'container'}
            filter={showFilter &&
                <PropertyFilter
                    filteringPlaceholder="Find CLI"
                    expandToViewport
                query={filterQuery}
                    onChange={({detail}) => setFilteringQuery(detail)}
                    countText={isLoading ? '' : `${items.length} match${items.length > 1 ? 'es' : ''}`}
                filteringProperties={filteringProperties as any}
                filteringOptions={filteringOptions}
                />
            }
            onSortingChange={({ detail }) => {
                setSortingDescending(detail.isDescending);
                setSortingColumn(detail.sortingColumn);
                if (onSortingChanged){
                    onSortingChanged(detail.isDescending? DbSortOrder.DESCENDING: DbSortOrder.ASCENDING, detail.sortingColumn.sortingField);
                }
            } }   
        />
        <OpenCalculationReviewCommentModal
            calculationReview={reviewItemForComments}
            comments={getCommentsResult}
            visible={showCommentsModal}
            isSavingComment={isSavingComment}
            saveCommentError={saveCommentError}
            onSaveComment={saveComment}
            saveNotificationCommentResult={saveCommentResult}
            onClose={()=> setShowCommentsModal(false)}/>
      </>
    )
}


